import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import ApiClient from "#/redux/api/ApiClient";
import { handleLoadingAndErrorState } from "#/redux/util/reduxUtils";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/zival/data";
const currentAnimalBaseUrl = `${baseUrl}/currentForZivalMat`;

export const createAnimalData = createAsyncThunk(
  "animalDataCreate/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(baseUrl, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Re-added from the former AnimalDataCreateSlice.js for list/search scenarios.
export const getAnimalDataPage = createAsyncThunk(
  "animalData/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        true
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getCurrentAnimalData = createAsyncThunk(
  "animalData/getCurrent",
  async ({ animalId }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${currentAnimalBaseUrl}/${animalId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getAnimalDataAsOf = createAsyncThunk(
  "animalData/getAsOf",
  async ({ animalId, asOf }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/forZivalMat/${animalId}`,
        {
          asOf,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateAnimalData = createAsyncThunk(
  "animalData/update",
  async ({ animalId, dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(
        `${currentAnimalBaseUrl}/${animalId}`,
        dto
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getCurrentAnimalDataForZivalMat = getCurrentAnimalData;
export const updateCurrentAnimalDataForZivalMat = updateAnimalData;

const animalDataSlice = createSlice({
  name: "animalDataCreate",
  initialState: {
    isLoading: false,
    error: null,
    lastCreated: null,
    page: null,
    currentAnimalData: null,
    isCurrentAnimalDataLoading: false,
    currentAnimalDataError: null,
    isCurrentAnimalDataSaving: false,
    currentAnimalDataSaveError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createAnimalData.fulfilled, (state, action) => {
        state.lastCreated = action.payload;
      })
      .addCase(getAnimalDataPage.fulfilled, (state, action) => {
        state.page = action.payload;
      })
      .addCase(getCurrentAnimalDataForZivalMat.pending, (state) => {
        state.isCurrentAnimalDataLoading = true;
        state.currentAnimalDataError = null;
      })
      .addCase(getCurrentAnimalDataForZivalMat.fulfilled, (state, action) => {
        state.isCurrentAnimalDataLoading = false;
        state.currentAnimalData = action.payload;
      })
      .addCase(getCurrentAnimalDataForZivalMat.rejected, (state, action) => {
        state.isCurrentAnimalDataLoading = false;
        state.currentAnimalDataError = action.payload;
      })
      .addCase(updateCurrentAnimalDataForZivalMat.pending, (state) => {
        state.isCurrentAnimalDataSaving = true;
        state.currentAnimalDataSaveError = null;
      })
      .addCase(
        updateCurrentAnimalDataForZivalMat.fulfilled,
        (state, action) => {
          state.isCurrentAnimalDataSaving = false;
          state.currentAnimalData = action.payload;
        }
      )
      .addCase(updateCurrentAnimalDataForZivalMat.rejected, (state, action) => {
        state.isCurrentAnimalDataSaving = false;
        state.currentAnimalDataSaveError = action.payload;
      });

    handleLoadingAndErrorState(builder, [
      createAnimalData,
      getAnimalDataPage,
      getCurrentAnimalData,
      getAnimalDataAsOf,
      updateAnimalData,
    ]);
  },
});

export default animalDataSlice.reducer;
